import { useState, useEffect, useMemo } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useScraperSettings } from "@/hooks/useScraperSettings";

interface SearchFiltersProps {
  onFilterChange: (platforms: string[], hideOutOfStock: boolean, selectedBrands: string[]) => void;
  availableResults: any[];
}

const SearchFilters = ({ onFilterChange, availableResults }: SearchFiltersProps) => {
  const { scrapers, isLoading: loading } = useScraperSettings();
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [hideOutOfStock, setHideOutOfStock] = useState(true);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);

  // Extract unique brands from results
  const uniqueBrands = useMemo(() => {
    const brands = new Set<string>();
    availableResults.forEach((result: any) => {
      if (result.brand && result.brand.trim() !== "") {
        brands.add(result.brand.trim());
      }
    });
    return Array.from(brands).sort();
  }, [availableResults]);

  // Update selected platforms when config changes
  useEffect(() => {
    if (!loading && scrapers.length > 0) {
      const activePlatforms = scrapers.filter(s => s.enabled).map(s => s.scraper_name);
      setSelectedPlatforms(activePlatforms);
      onFilterChange(activePlatforms, hideOutOfStock, selectedBrands);
    }
  }, [scrapers, loading]);

  // Reset selected brands when results change
  useEffect(() => {
    setSelectedBrands([]);
  }, [availableResults]);

  const handlePlatformToggle = (platform: string) => {
    const updated = selectedPlatforms.includes(platform)
      ? selectedPlatforms.filter(p => p !== platform)
      : [...selectedPlatforms, platform];
    
    setSelectedPlatforms(updated);
    onFilterChange(updated, hideOutOfStock, selectedBrands);
  };

  const handleBrandToggle = (brand: string) => {
    const updated = selectedBrands.includes(brand)
      ? selectedBrands.filter(b => b !== brand)
      : [...selectedBrands, brand];
    
    setSelectedBrands(updated);
    onFilterChange(selectedPlatforms, hideOutOfStock, updated);
  };

  const handleSelectAllBrands = () => {
    setSelectedBrands(uniqueBrands);
    onFilterChange(selectedPlatforms, hideOutOfStock, uniqueBrands);
  };

  const handleDeselectAllBrands = () => {
    setSelectedBrands([]);
    onFilterChange(selectedPlatforms, hideOutOfStock, []);
  };

  const handleStockToggle = (checked: boolean) => {
    setHideOutOfStock(checked);
    onFilterChange(selectedPlatforms, checked, selectedBrands);
  };

  const isPlatformEnabled = (platformName: string) => {
    const config = scrapers.find(s => s.scraper_name === platformName);
    return config ? config.enabled : true;
  };

  return (
    <div className="space-y-4 animate-fade-in">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-5 backdrop-blur-md bg-background/50 border-2 border-border/50 rounded-xl hover:border-primary/30 hover:bg-background/70 transition-all duration-300 group"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary-glow/10 flex items-center justify-center group-hover:scale-110 transition-transform">
            <ChevronDown className={`h-4 w-4 text-primary transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
          </div>
          <span className="text-foreground font-semibold text-base font-mono uppercase tracking-wider">Filtres de recherche</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground font-mono">
          {selectedPlatforms.length > 0 && (
            <span className="px-2 py-1 bg-primary/10 text-primary rounded-md">
              {selectedPlatforms.length} plateformes
            </span>
          )}
          {selectedBrands.length > 0 && (
            <span className="px-2 py-1 bg-accent/10 text-accent rounded-md">
              {selectedBrands.length} marques
            </span>
          )}
          {hideOutOfStock && (
            <span className="px-2 py-1 bg-green-500/10 text-green-500 rounded-md">
              Stock uniquement
            </span>
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="backdrop-blur-md bg-background/50 border-2 border-border/50 rounded-xl p-8 space-y-8 animate-scale-in shadow-lg">
          {/* Platforms Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <div className="h-1 w-8 bg-gradient-to-r from-primary to-primary-glow rounded-full"></div>
              <h4 className="text-sm font-bold text-foreground uppercase tracking-wider font-mono">
                Plateformes
              </h4>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {scrapers.map((scraper) => {
                const isEnabled = scraper.enabled;
                const isChecked = selectedPlatforms.includes(scraper.scraper_name);
                
                return (
                  <label
                    key={scraper.scraper_name}
                    className={`relative flex items-center space-x-3 p-4 rounded-lg border-2 transition-all cursor-pointer hover:scale-[1.02] ${
                      isChecked 
                        ? 'border-primary bg-primary/5 shadow-md' 
                        : 'border-border/50 hover:border-border bg-background/50'
                    } ${!isEnabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                  >
                    <Checkbox
                      id={scraper.scraper_name}
                      checked={isChecked}
                      onCheckedChange={() => handlePlatformToggle(scraper.scraper_name)}
                      className="border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                      disabled={!isEnabled}
                    />
                    <div className="flex-1 min-w-0">
                      <span className={`text-sm font-medium block truncate ${
                        isChecked ? 'text-primary' : 'text-foreground'
                      } ${!isEnabled ? 'line-through' : ''}`}>
                        {scraper.scraper_name}
                      </span>
                      {!isEnabled && (
                        <span className="text-xs text-muted-foreground block mt-0.5">
                          Indisponible
                        </span>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Brands Section */}
          <div className="space-y-4 pt-6 border-t-2 border-border/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-1 w-8 bg-gradient-to-r from-accent to-accent/50 rounded-full"></div>
                <h4 className="text-sm font-bold text-foreground uppercase tracking-wider font-mono">
                  Marques 
                  {selectedBrands.length > 0 && (
                    <span className="ml-2 text-xs text-accent font-normal">
                      ({selectedBrands.length} / {uniqueBrands.length})
                    </span>
                  )}
                </h4>
              </div>
              {uniqueBrands.length > 0 && (
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleSelectAllBrands}
                    className="text-xs font-medium text-primary hover:text-primary-glow transition-colors px-3 py-1.5 rounded-md hover:bg-primary/5"
                  >
                    Tout sélectionner
                  </button>
                  <button
                    type="button"
                    onClick={handleDeselectAllBrands}
                    className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors px-3 py-1.5 rounded-md hover:bg-muted"
                  >
                    Réinitialiser
                  </button>
                </div>
              )}
            </div>
            {uniqueBrands.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {uniqueBrands.map((brand) => {
                  const isChecked = selectedBrands.includes(brand);
                  return (
                    <label
                      key={brand}
                      className={`relative flex items-center space-x-3 p-4 rounded-lg border-2 transition-all cursor-pointer hover:scale-[1.02] ${
                        isChecked 
                          ? 'border-accent bg-accent/5 shadow-md' 
                          : 'border-border/50 hover:border-border bg-background/50'
                      }`}
                    >
                      <Checkbox
                        id={`brand-${brand}`}
                        checked={isChecked}
                        onCheckedChange={() => handleBrandToggle(brand)}
                        className="border-border data-[state=checked]:bg-accent data-[state=checked]:border-accent"
                      />
                      <span className={`text-sm font-medium truncate ${
                        isChecked ? 'text-accent' : 'text-foreground'
                      }`}>
                        {brand}
                      </span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 px-4 bg-muted/20 rounded-lg border-2 border-dashed border-border/50">
                <p className="text-sm text-muted-foreground font-medium">
                  Effectuez une recherche pour voir les marques disponibles
                </p>
              </div>
            )}
          </div>

          {/* Stock Filter */}
          <div className="pt-6 border-t-2 border-border/30">
            <label className="flex items-center gap-4 p-4 rounded-lg border-2 border-border/50 bg-background/50 hover:border-primary/30 hover:bg-background/70 cursor-pointer transition-all group">
              <Checkbox
                id="hideOutOfStock"
                checked={hideOutOfStock}
                onCheckedChange={handleStockToggle}
                className="border-border data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
              />
              <div className="flex-1">
                <span className="text-sm font-semibold text-foreground block">
                  Masquer les produits en rupture de stock
                </span>
                <span className="text-xs text-muted-foreground mt-1 block">
                  Afficher uniquement les produits disponibles
                </span>
              </div>
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchFilters;
